/* eslint-disable @typescript-eslint/no-misused-promises, @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-use-before-define */
import { constant, drop, identity, range, take } from "../util";
/**
 * Creates a Task which can be resolved/rejected externally.
 */
export const external = () => new ExternalTask();
export const emitter = (fn) => {
    const task = external();
    return [
        task,
        (...args) => {
            try {
                task.resolve(fn(...args));
            }
            catch (e) {
                task.reject(e);
            }
        },
    ];
};
/**
 * Creates a Task which has already successfully completed with `result`.
 * @alias of
 * @alias ok
 * @param result The value to place into the successful Task.
 */
export const succeed = (result) => new Task((_, resolve) => resolve(result));
export const of = succeed;
/**
 * Creates a Task which succeeds when forked.
 * @param result The function which will produce the result.
 */
export const succeedBy = (result) => new Task((reject, resolve) => {
    try {
        resolve(result());
    }
    catch (e) {
        reject(e);
    }
});
export const try_ = succeedBy;
/**
 * Creates a Task has an empty result.
 * @alias unit
 */
export const empty = () => of(void 0);
/**
 * Creates a Task which automatically succeeds at some time in the future with `result`.
 * @param ms How many milliseconds until it succeeds.
 * @param result The value to place into the successful Task.
 */
export const succeedIn = (ms, result) => new Task((_, resolve) => setTimeout(() => resolve(result), ms));
/**
 * Creates a Task which has already failed with `error`.
 * @alias err
 * @param error The error to place into the failed Task.
 */
export const fail = (error) => new Task(reject => reject(error));
/**
 * Creates a Task which automatically fails at some time in the future with `error`.
 *  @param ms How many milliseconds until it succeeds.
 * @param error The error to place into the failed Task.
 */
export const failIn = (ms, error) => new Task(reject => setTimeout(() => reject(error), ms));
/**
 * Creates a Task will never finish.
 */
export const never = () => new Task(() => void 0);
/**
 * Execute task computation and call handlers on completion.
 * @param reject Function to call on failure.
 * @param resolve Function to call on success.
 * @param task The task to fork.
 */
export const fork = (reject, resolve, task) => task.fork(reject, resolve);
/**
 * Chain a task to run after a previous task has succeeded.
 * @param fn Takes a successful result and returns a new task.
 * @param task The task which will chain to the next one on success.
 */
export const chain = (fn, task) => new Task((reject, resolve) => task.fork(reject, b => fn(b).fork(reject, resolve)));
/**
 * When forked, run a function which can check whether the task has already succeeded.
 * @param fn The function which either returns a success value or undefined.
 * @param task The task to run if the check fails (returns undefined).
 */
export const succeedIf = (fn, task) => new Task((reject, resolve) => {
    const result = fn();
    if (result) {
        resolve(result);
        return;
    }
    task.fork(reject, resolve);
});
/**
 * A task which only runs once. Caches the success or failure. Be careful.
 * @alias share
 * @param task The task to cache results.
 */
export const onlyOnce = (task) => {
    let state = "initialized";
    let cachedResult;
    let cachedError;
    let callbackId = 0;
    const callbacks = {};
    const notify = (reject, resolve) => {
        const id = callbackId++;
        callbacks[id] = { reject, resolve };
    };
    const triggerReject = (error) => {
        state = "failure";
        cachedError = error;
        Object.keys(callbacks).forEach(id => {
            callbacks[id].reject(error);
            delete callbacks[id];
        });
    };
    const triggerResolve = (result) => {
        state = "success";
        cachedResult = result;
        Object.keys(callbacks).forEach(id => {
            callbacks[id].resolve(result);
            delete callbacks[id];
        });
    };
    return new Task((reject, resolve) => {
        switch (state) {
            case "success":
                resolve(cachedResult);
                break;
            case "failure":
                reject(cachedError);
                break;
            case "pending":
                notify(reject, resolve);
                break;
            case "initialized":
                state = "pending";
                notify(reject, resolve);
                task.fork(triggerReject, triggerResolve);
        }
    });
};
export const share = onlyOnce;
/**
 * Given a promise, create a Task which relies on it.
 * @param promise The promise we will gather the success from.
 */
export const fromPromise = (maybePromise) => maybePromise instanceof Promise
    ? new Task((reject, resolve) => maybePromise.then(resolve, reject))
    : of(maybePromise);
/**
 * Given an array of promises, create a Task which relies on it.
 * @param promise The promises we will gather the success from.
 */
export const fromPromises = (promises) => all(promises.map(fromPromise));
/**
 * Take a function which generates a promise and lazily execute it.
 * @param getPromise The getter function
 */
export const fromLazyPromise = (getPromise) => succeedBy(getPromise).chain(fromPromise);
/**
 * Given a function that returns a promise, return a new function that
 * lazily returns a Task instead.
 * @param fn A function which returns a promise
 */
export const wrapPromiseCreator = (fn) => (...args) => fromLazyPromise(() => fn(...args));
/**
 * Given a task, create a Promise which resolves when the task does.
 * @param task The task we will convert to a promise.
 */
export const toPromise = (task) => new Promise((resolve, reject) => task.fork(reject, resolve));
/**
 * Given an array of tasks, return the one which finishes first.
 * @alias select
 * @param tasks The tasks to run in parallel.
 */
export const race = (tasks) => new Task((reject, resolve) => {
    let done = false;
    return tasks.map(task => task.fork((error) => {
        if (done) {
            return;
        }
        done = true;
        reject(error);
    }, (result) => {
        if (done) {
            return;
        }
        done = true;
        resolve(result);
    }));
});
export class LoopBreak {
    constructor(value) {
        this.value = value;
    }
}
export class LoopContinue {
    constructor(value) {
        this.value = value;
    }
}
/**
 * Given an initialValue, asynchronously loop until either a value is
 * resolved by returning a Task<E, LoopBreak<S>>.
 * @param fn A function that takes the current loop value and decides whether to continue or break.
 * @param initialValue The initial value.
 */
export const loop = (fn, initialValue) => new Task((reject, resolve) => {
    const tryLoop = (currentValue) => {
        fn(currentValue).fork(err => {
            reject(err);
        }, result => {
            if (result instanceof LoopBreak) {
                resolve(result.value);
            }
            if (result instanceof LoopContinue) {
                tryLoop(result.value);
            }
        });
    };
    tryLoop(initialValue);
});
/**
 * An async reducer. Given an initial return value and an array of
 * items to sequentially loop over, pass each step through a reducer
 * function which returns a Task of the next reduced value.
 * @param fn
 * @param initialValue
 * @param items
 */
export const reduce = (fn, initialValue, items) => loop(({ remainingItems, currentResult }) => {
    if (remainingItems.length === 0) {
        return of(new LoopBreak(currentResult));
    }
    const [head, ...tail] = remainingItems;
    const index = items.length - tail.length - 1;
    return fn(currentResult, head, index, items).map(nextResult => new LoopContinue({ remainingItems: tail, currentResult: nextResult }));
}, { remainingItems: items, currentResult: initialValue });
/**
 * Given an array of tasks, return the one which finishes successfully first.
 * @param tasks The tasks to run in parallel.
 */
export const firstSuccess = (tasks) => tasks.length === 0
    ? fail([])
    : new Task((reject, resolve) => {
        let isDone = false;
        let runningTasks = tasks.length;
        const errors = [];
        return tasks.map(task => task.fork((error) => {
            if (isDone) {
                return;
            }
            runningTasks -= 1;
            errors.push(error);
            if (runningTasks === 0) {
                reject(errors);
            }
        }, (result) => {
            if (isDone) {
                return;
            }
            isDone = true;
            resolve(result);
        }));
    });
/**
 * Given an array of task which return a result, return a new task which returns an array of results.
 * @alias collect
 * @param tasks The tasks to run in parallel.
 */
export const all = (tasks) => sequence(tasks, Infinity);
/**
 * Given an array of task which return a result, return a new task which returns an array of successful results.
 * @param tasks The tasks to run in parallel.
 */
export const allSuccesses = (tasks) => tasks.length === 0
    ? of([])
    : new Task((_reject, resolve) => {
        let runningTasks = tasks.length;
        const results = [];
        return tasks.map(task => task.fork(() => {
            runningTasks -= 1;
            if (runningTasks === 0) {
                resolve(results);
            }
        }, (result) => {
            runningTasks -= 1;
            results.push(result);
            if (runningTasks === 0) {
                resolve(results);
            }
        }));
    });
/**
 * Creates a task that waits for two tasks of different types to
 * resolve as a two-tuple of the results.
 * @param taskA The first task.
 * @param taskB The second task.
 */
export const zip = (taskA, taskB) => map2(a => b => [a, b], taskA, taskB);
/**
 * Creates a task that waits for two tasks of different types to
 * resolve, then passing the resulting two-tuple of results through
 * a mapping function.
 * @param fn
 * @param taskA The first task.
 * @param taskB The second task.
 */
export const zipWith = (fn, taskA, taskB) => map2(a => b => fn(a, b), taskA, taskB);
/**
 * Given an array of task which return a result, return a new task which results an array of results.
 * @param tasks The tasks to run in sequence.
 */
export const sequence = (tasks, maxConcurrent = 1) => new Task((reject, resolve) => {
    let isDone = false;
    let queue = tasks.map((task, i) => [task, i]);
    const inflight = new Set();
    const results = [];
    const enqueue = () => {
        if (isDone) {
            return;
        }
        if (queue.length <= 0 && inflight.size <= 0) {
            isDone = true;
            resolve(results);
            return;
        }
        const howMany = Math.min(queue.length, maxConcurrent - inflight.size);
        const readyTasks = take(howMany, queue);
        queue = drop(howMany, queue);
        readyTasks.forEach(([task, i]) => {
            inflight.add(task);
            task.fork((error) => {
                if (isDone) {
                    return;
                }
                isDone = true;
                reject(error);
            }, (result) => {
                results[i] = result;
                inflight.delete(task);
                enqueue();
            });
        });
    };
    enqueue();
});
/**
 * Given a task, swap the error and success values.
 * @param task The task to swap the results of.
 */
export const swap = (task) => new Task((reject, resolve) => task.fork(e => resolve(e), s => reject(s)));
/**
 * Given a task, map the successful value to a Task.
 * @param fn A function which takes the original successful result and returns the new one.
 * @param task The task to map the succcessful result.
 */
export const map = (fn, task) => new Task((reject, resolve) => task.fork(reject, result => resolve(fn(result))));
export const map2 = (fn, taskA, taskB) => Task.of(fn).ap(taskA).ap(taskB);
export const map3 = (fn, taskA, taskB, taskC) => Task.of(fn).ap(taskA).ap(taskB).ap(taskC);
export const map4 = (fn, taskA, taskB, taskC, taskD) => Task.of(fn).ap(taskA).ap(taskB).ap(taskC).ap(taskD);
/**
 * Run a side-effect on success. Useful for logging.
 * @param fn A function will fire with the successful value.
 * @param task The task to tap on succcess.
 */
export const tap = (fn, task) => map(result => {
    fn(result);
    return result;
}, task);
/**
 * Run an additional task on success. Useful for async side-effects.
 * @alias defer
 * @param fn A function will fire with the successful value.
 * @param task The task to tap on succcess.
 */
export const tapChain = (fn, task) => chain(result => fn(result).forward(result), task);
/**
 * Run a function on a successful value which can fail the task or modify the type.
 * @param fn A function will return a Validation on the value.
 * @param task The task to tap on succcess.
 */
export const validate = (fn, task) => chain((value) => {
    const result = fn(value);
    return result.success ? of(result.value) : fail(result.error);
}, task);
/**
 * Given a task, map the failure error to a Task.
 * @alias recoverWith
 * @alias rescue
 * @param fn A function which takes the original failure error and returns the new one.
 * @param task The task to map the failure.
 */
export const mapError = (fn, task) => new Task((reject, resolve) => task.fork(error => reject(fn(error)), resolve));
export const validateError = (fn, task) => mapError(err => {
    if (!fn(err)) {
        throw new Error(`validateError failed`);
    }
    return err;
}, task);
export const errorUnion = (task) => task;
/**
 * Given a task, map both the failure error and the success result to a Task.
 * @param handleError A function which takes the original failure error and returns the new one.
 * @param handleSuccess A function which takes the original successful result and returns the new one.
 * @param task The task to map the failure and succeess of.
 */
export const mapBoth = (handleError, handleSuccess, task) => mapError(handleError, map(handleSuccess, task));
/**
 * Given a task, map both the failure error and the success result to a Task which always succeeds.
 * @param handleError A function which takes the original failure error and returns a successful result.
 * @param handleSuccess A function which takes the original successful result and returns a new successful result.
 * @param task The task to map failure and succeess to a success for.
 */
export const fold = (handleError, handleSuccess, task) => new Task((_, resolve) => task.fork(error => resolve(handleError(error)), result => resolve(handleSuccess(result))));
/**
 * Given a task, if the result in a failure, attemp to generate another Task from the error.
 * @param fn A function which takes the original failure error and returns a Task.
 * @param task The task to try to run a recovery function on failure.
 */
export const orElse = (fn, task) => new Task((reject, resolve) => task.fork(error => fn(error).fork(reject, resolve), resolve));
/**
 * Given a task that succeeds with a map function as its result,
 * run that function over the result of a second successful Task.
 * @param appliedTask The task whose value will be passed to the map function.
 * @param task The task who will return a map function as the success result.
 */
export const ap = (task, appliedTask) => new Task((reject, resolve) => {
    let targetResult;
    let applierFunction;
    let hasResultLoaded = false;
    let isRejected = false;
    const handleResolve = (onResolve) => {
        return (x) => {
            if (isRejected) {
                return;
            }
            onResolve(x);
            if (applierFunction && hasResultLoaded) {
                resolve(applierFunction(targetResult));
            }
        };
    };
    const handleReject = (x) => {
        if (isRejected) {
            return;
        }
        isRejected = true;
        reject(x);
    };
    task.fork(handleReject, handleResolve((x) => {
        applierFunction = x;
    }));
    appliedTask.fork(handleReject, handleResolve((x) => {
        hasResultLoaded = true;
        targetResult = x;
    }));
});
/**
 * Wait some number of seconds to continue after a successful task.
 * @param ms How long to wait in milliseconds.
 * @param task Which task to wait to succeed with.
 */
export const wait = (ms, task) => new Task((reject, resolve) => {
    setTimeout(() => task.fork(reject, resolve), ms);
});
/**
 * If a task fails, retry it in the future.
 * @param ms How long to wait before trying.
 * @param task Which task to retry.
 */
export const retryIn = (ms, task) => task.orElse(() => task.wait(ms));
/**
 * If a task fails, retry it X times, with exponential backoff.
 * @param ms How long to wait before trying the first time.
 * @param times How many times to attempt, each waiting 2x the previous time.
 * @param task Which task to retry.
 */
export const retryWithExponentialBackoff = (ms, times, task) => range(times).reduce((sum, i) => sum.retryIn(ms * 2 ** i), task);
/**
 * Takes a nested task of tasks, which often comes from a map, and
 * flattens to just the resulting chained task.
 * @param task The task which resolves to an other task.
 */
export const flatten = (task) => task.chain(identity);
/**
 * Given a predicate, if it returns true, error the task with a given value.
 * @param pred Run this on a successful task, return true to fail the task.
 * @param error If the predicate succeeded, run this function to get the error result.
 */
export const failIf = (pred, error, task) => task.chain(result => (pred(result) ? fail(error(result)) : of(result)));
/**
 * Create a new task.
 * @param computation A function which will be run when the task starts.
 */
export class Task {
    constructor(computation) {
        this.computation = computation;
        this.isCanceled = false;
    }
    fork(reject, resolve) {
        let localCancel = this.isCanceled;
        const result = {
            cancel: () => (localCancel = true),
        };
        if (localCancel) {
            return result;
        }
        this.computation(err => {
            if (!localCancel) {
                reject(err);
            }
        }, value => {
            if (!localCancel) {
                resolve(value);
            }
        });
        return result;
    }
    cancel() {
        this.isCanceled = true;
    }
    /**
     * Alias to match promise API and let async/await work.
     * Mostly "private". Do not use.
     */
    then(onfulfilled, onrejected) {
        return this.toPromise().then(onfulfilled, onrejected);
    }
    chain(fn) {
        return chain(fn, this);
    }
    succeedIf(fn) {
        return succeedIf(fn, this);
    }
    onlyOnce() {
        return onlyOnce(this);
    }
    toPromise() {
        return toPromise(this);
    }
    swap() {
        return swap(this);
    }
    map(fn) {
        return map(fn, this);
    }
    forward(value) {
        return map(constant(value), this);
    }
    append(...items) {
        return map(a => [a, ...items], this);
    }
    prepend(...items) {
        return map(a => [...items, a], this);
    }
    tap(fn) {
        return tap(fn, this);
    }
    tapChain(fn) {
        return tapChain(fn, this);
    }
    validate(fn) {
        return validate(fn, this);
    }
    mapError(fn) {
        return mapError(fn, this);
    }
    validateError(fn) {
        return validateError(fn, this);
    }
    errorUnion() {
        return errorUnion(this);
    }
    mapBoth(handleError, handleSuccess) {
        return mapBoth(handleError, handleSuccess, this);
    }
    fold(handleError, handleSuccess) {
        return fold(handleError, handleSuccess, this);
    }
    orElse(fn) {
        return orElse(fn, this);
    }
    ap(task) {
        return ap(this, task);
    }
    wait(ms) {
        return wait(ms, this);
    }
    retryIn(ms) {
        return retryIn(ms, this);
    }
    retryWithExponentialBackoff(ms, times) {
        return retryWithExponentialBackoff(ms, times, this);
    }
    flatten() {
        return flatten(this);
    }
    failIf(pred, error) {
        return failIf(pred, error, this);
    }
}
Task.fail = fail;
Task.succeed = succeed;
Task.empty = empty;
Task.failIn = failIn;
Task.succeedIn = succeedIn;
Task.of = succeed;
Task.all = all;
Task.allSuccesses = allSuccesses;
Task.sequence = sequence;
Task.firstSuccess = firstSuccess;
Task.never = never;
Task.fromPromise = fromPromise;
Task.fromPromises = fromPromises;
Task.fromLazyPromise = fromLazyPromise;
Task.wrapPromiseCreator = wrapPromiseCreator;
Task.race = race;
Task.external = external;
Task.emitter = emitter;
Task.succeedBy = succeedBy;
Task.ap = ap;
Task.map2 = map2;
Task.map3 = map3;
Task.map4 = map4;
Task.loop = loop;
Task.reduce = reduce;
Task.zip = zip;
Task.zipWith = zipWith;
Task.flatten = flatten;
/**
 * A special form of Task which can be resolved/rejected externally.
 */
export class ExternalTask extends Task {
    constructor() {
        super((reject, resolve) => {
            switch (this.lastState_) {
                case "error":
                    reject(this.alreadyError_);
                case "success":
                    resolve(this.alreadyResult_);
                case "pending":
                    this.computationReject_ = reject;
                    this.computationResolve_ = resolve;
            }
        });
        this.lastState_ = "pending";
    }
    reject(error) {
        this.alreadyError_ = error;
        this.lastState_ = "error";
        if (this.computationReject_) {
            this.computationReject_(error);
        }
    }
    resolve(result) {
        this.alreadyResult_ = result;
        this.lastState_ = "success";
        if (this.computationResolve_) {
            this.computationResolve_(result);
        }
    }
}
Promise.prototype.toTask = function () {
    return fromPromise(this);
};
